import React, { useCallback, useEffect, useState } from 'react';

import Alert, { AlertVariant } from '../../components/Alert';
import { useNavigate } from 'react-router-dom';
import AsyncSelect from 'react-select/async';
import { components, CSSObjectWithLabel } from 'react-select';

import Search from '../../assets/svg/search.svg';

import StepInterface from '../../interfaces/Step';
import HtmlInput from '../../interfaces/HtmlInput';
import RequestStatus from '../../interfaces/RequestStatus';
import StepClass from '../../entities/Step';

import validations from '../../helpers/Validations';
import { responseMensagemStatus } from '../../helpers/translate';

import ProgressStep from '../../components/ProgressStep';
import ProgressBar from '../../components/ProgressBar';
import Tooltip from '../../components/Tooltip';
import Input from '../../components/Input';
import Button from '../../components/Button';
import Typography from '../../components/Typography';

import {
  gtmAttributesAcceptedBanks,
  gtmAttributesTooltipAccount,
  gtmAttributesTooltipAgency,
  gtmAttributesUpdateBankDataPage,
} from '../../utils/gtmVariables';

import BankService from '../../services/BankService';
import palette from '../../theme/gcbPalette';

import useGTMEventsTracker from '../../hooks/useGTMEventsTracker';
import AppConfig from '../../config/AppConfig';
import url from '../../url';

interface DataBank {
  bank: string;
  agency: string;
  agency_digit: string;
  account: string;
  account_digit: string;
}

interface BankProps {
  code: string | number;
  label: string;
  value: string;
}

interface BankItemResponse {
  codBank: string | number;
  nameBank: string;
}

interface SuggestionBankProps {
  code: string;
  name: string;
}

const Bank = new BankService();
const Step = new StepClass();

const BankData = () => {
  const [data, setData] = useState<DataBank>({} as DataBank);
  const [selected, setSelected] = useState<{
    label: string | null;
    value: string | null;
    code: string | null;
  }>({ label: null, value: null, code: null });
  const [options, setOptions] = useState<Array<BankProps>>([]);
  const [showMessage, setShowMessage] = useState(false);
  const [message, setMessage] = useState('Carregando...');
  const [variant, setVariant] = useState<AlertVariant>('success');
  const [selectLabel, setSelectLabel] = useState('Carregando opções de bancos');
  const [validBankData, setValidBankData] = useState({
    agency: false,
    account: false,
  });
  const [focused, setFocused] = useState(false);
  const [suggestionBank, setSuggestionBank] = useState<SuggestionBankProps>({
    code: '',
    name: '',
  } as SuggestionBankProps);
  const [disableButton, setDisableButton] = useState(false);

  const navigate = useNavigate();
  const gtmEventsTracker = useGTMEventsTracker();
  const { ValueContainer, Placeholder } = components;

  const acceptBanksLink = `${url.base + url.bank.listAcceptedBanks}`;
  const current: number = 3;
  const steps: Array<StepInterface> = Step.steps();

  const noBankOption = {
    code: '0',
    value: 'Não encontrei meu banco',
    label: 'Não encontrei meu banco',
  };

  useEffect(() => {
    gtmEventsTracker(gtmAttributesUpdateBankDataPage);
  }, []);

  useEffect(() => {
    Bank.getBanks({ limit: 500 })
      .then((response: any) => {
        const banks = response.data;
        const bankOptions = banks.map(({ nameBank, codBank }: BankItemResponse) => ({
          code: codBank,
          value: nameBank,
          label: nameBank,
        }));
        bankOptions.push(noBankOption);
        setOptions(bankOptions);
        setSelectLabel('Selecione o seu banco');
      })
      .catch(() => {
        setSelectLabel('Não foi possível carregar os bancos disponíveis');
      });
  }, []);

  const handleSubmit = async (event: any) => {
    event.preventDefault();
    if (
      data.account &&
      data.account !== '' &&
      data.account_digit &&
      data.account_digit !== '' &&
      data.agency &&
      data.agency !== '' &&
      selected?.value
    ) {
      setShowMessage(true);
      setDisableButton(true);
      try {
        await Bank.addBankData({
          bank: {
            ...data,
            code: selected.code,
            name: selected.value,
          },
        });
        navigate('/step/product/integration');
      } catch {
        setVariant('danger');
        setMessage('Erro ao atualizar dados bancários.');
        setDisableButton(false);
      }
      return;
    }
    setVariant('warning');
    setMessage('Preencha todos os campos do formulário.');
  };

  const onlyNumber = (number: string) => number.replace(/[^0-9]/g, '');

  const handleChangeBankSuggestion = useCallback(
    (e: any) => {
      const nameInput = e?.target?.name;
      const value = e?.target?.value;
      if (e.target && typeof e.target.value !== undefined) {
        setSuggestionBank({
          ...suggestionBank,
          [nameInput]: ['code'].includes(nameInput) ? onlyNumber(value) : value,
        });
      }
    },
    [suggestionBank]
  );

  const handleChange = useCallback(
    (e: any) => {
      const nameInput = e?.target?.name;
      const value = e?.target?.value;
      setValidBankData({
        ...validBankData,
        [nameInput]: e.isValid,
      });
      if (e.target && typeof e.target.value !== undefined) {
        setData({
          ...data,
          [nameInput]: ['agency', 'agency_digit', 'account', 'account_digit'].includes(nameInput)
            ? onlyNumber(value)
            : value,
        });
      }
    },
    [data]
  );

  const DropdownSelectIndicator = () => {
    return (
      <div className="dropdownIndicator">
        <img src={Search} alt="" width={20} />
      </div>
    );
  };

  const selectChanged = (selectedOptions: any) => {
    setSelected(selectedOptions);
  };

  const bankSuggestionValidation = (error: RequestStatus) => {
    if (error && error?.response?.status !== 200) {
      setDisableButton(false);
      setVariant('danger');
      setMessage(
        error.response.data
          ? responseMensagemStatus[error.response.data]
          : 'Erro ao atualizar dados bancários'
      );
      return;
    }
    setVariant('success');
    setMessage('Sugestão cadastrada com sucesso!');
    setSuggestionBank({
      code: '',
      name: '',
    });
    setTimeout(() => {
      location.reload();
    }, 1000);
  };

  const registerBankSuggestion = async () => {
    setShowMessage(true);
    setDisableButton(true);
    if (
      suggestionBank.code &&
      suggestionBank.code !== '' &&
      suggestionBank.name &&
      suggestionBank.name !== ''
    ) {
      await Bank.registerBankSuggestion(suggestionBank, bankSuggestionValidation);
      return;
    }
    setDisableButton(false);
    setVariant('warning');
    setMessage('Preencha os campos do formulário');
  };

  const loadOptions = (searchValue: string, callback: (options: BankProps[]) => void) => {
    setTimeout(() => {
      const filterOptions = options.filter((option) =>
        option.label.toLowerCase().includes(searchValue.toLowerCase())
      );
      if (filterOptions.length === 0) {
        filterOptions.push(noBankOption);
      }
      callback(filterOptions);
    }, 1000);
  };

  const CustomValueContainer = ({ children, ...props }: any) => {
    return (
      <ValueContainer {...props}>
        <Placeholder {...props} isFocused>
          {props.selectProps.placeholder}
        </Placeholder>
        {React.Children.map(children, (child) =>
          child && child.key !== 'placeholder' ? child : null
        )}
      </ValueContainer>
    );
  };

  const selectStyle = {
    valueContainer: (provided: CSSObjectWithLabel) => ({
      ...provided,
      overflow: 'visible',
    }),
    placeholder: (base: CSSObjectWithLabel) => ({
      ...base,
      display: focused || selected.label != null ? 'none' : 'block',
      color: palette.neutral.dark,
      fontSize: 14,
      fontWeight: 400,
    }),
    control: (base: CSSObjectWithLabel) => ({
      ...base,
      borderRadius: 8,
      boxShadow: selected.label != null ? `0 0 0 1px ${palette.neutral.light}00` : '',
      borderColor: selected.label != null ? palette.neutral.light : '',
      ':hover': {
        borderColor: palette.neutral.light,
      },
      background: palette.support1.light,
    }),
    menuList: (base: CSSObjectWithLabel) => ({
      ...base,
      '::-webkit-scrollbar': {
        width: '6px',
        height: '0px',
        marginRight: 16,
      },
      '::-webkit-scrollbar-track': {
        background: palette.neutral.light,
        marginRight: 16,
      },
      '::-webkit-scrollbar-thumb': {
        background: palette.primary.medium,
        borderRadius: 10,
      },
    }),
    option: (styles: CSSObjectWithLabel, { isFocused, isSelected }: any) => ({
      ...styles,
      background: isFocused ? `${palette.primary.medium}1A` : isSelected ? '#fff' : '',
      color: '#000',
      fontWeight: isSelected ? '600' : '400',
      ':active': {
        background: palette.primary.medium,
      },
    }),
    indicatorsContainer: (base: CSSObjectWithLabel) => ({
      ...base,
      margin: 14,
    }),
  };

  const downloadAcceptedBanksPdf = () => {
    gtmEventsTracker(gtmAttributesAcceptedBanks);
    Bank.downloadFile(acceptBanksLink).catch(console.error);
  };

  return (
    <>
      <div>
        <div className="mb-12 max-[913px]:hidden">
          <ProgressStep steps={steps} current={current} />
        </div>
        <div className="hidden max-[913px]:block mb-12">
          <ProgressBar
            total={steps.length}
            step={current}
            subtitle={steps.find((e) => e.step === current)?.title ?? ''}
          />
        </div>
      </div>
      <div className="max-w-[29.5rem] pb-[5.688rem] mx-auto max-[500px]:block max-[500px]:px-5 max-[500px]:pb-[6.438rem]">
        <div className="text-center mb-12 max-[500px]:text-left">
          <div className="mb-[0.625rem]">
            <Typography variant="subtitle2" className="text-neutral-darkest">
              Informe os dados bancários do CNPJ cadastrado
            </Typography>
          </div>
          <Typography variant="body2" className="text-neutral-darkest">
            É por meio desta conta que vamos fazer os repasses das suas vendas e atrelar as notas de
            comissão.
          </Typography>
        </div>
        <form noValidate onSubmit={handleSubmit}>
          <div className="mb-6">
            <div className="mb-[10px]">
              <label className="font-normal text-xs text-neutral-dark">Banco*</label>
              <button
                type="button"
                className="text-support2-medium ml-2"
                onClick={(e) => {
                  e.preventDefault();
                  downloadAcceptedBanksPdf();
                }}
              >
                Confira os bancos aceitos
              </button>
            </div>
            <AsyncSelect
              placeholder="Pesquise pelo código ou nome"
              loadOptions={loadOptions}
              onChange={selectChanged}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              defaultOptions={options}
              components={{
                DropdownIndicator: DropdownSelectIndicator,
                ValueContainer: CustomValueContainer,
              }}
              id="mp__bank-select2"
              styles={selectStyle}
              loadingMessage={() => 'Carregando...'}
            />
          </div>
          {selected.label === 'Não encontrei meu banco' && (
            <>
              <div className="flex flex-col gap-7 mb-6">
                <Typography as="p" variant="body2" className="leading-6">
                  Para continuar com o cadastro, a gente recomenda que você crie uma conta PJ em um
                  dos bancos aceitos aqui pelo nosso marketplace.{' '}
                  <button
                    type="button"
                    className="text-support2-medium font-bold"
                    onClick={(e) => {
                      e.preventDefault();
                      downloadAcceptedBanksPdf();
                    }}
                  >
                    Confira aqui a lista completa de bancos aceitos.
                  </button>
                </Typography>
                <Typography as="p" variant="body2" className="leading-6">
                  Você pode sugerir um banco ainda não aceito. Assim, a gente te avisa quando ele
                  estiver cadastrado.
                </Typography>
              </div>
              <div className="flex gap-4 mb-12 max-[900px]:flex-col">
                <div>
                  <Input
                    label="Código do banco"
                    id="mp__bank-code"
                    type="text"
                    name="code"
                    className="w-[8.75rem] max-[900px]:w-full"
                    maxLength={3}
                    onChange={handleChangeBankSuggestion}
                    value={suggestionBank.code}
                    rules={[(val: HtmlInput) => validations.required(val, 'Código do banco')]}
                  />
                </div>
                <div>
                  <Input
                    label="Nome do banco"
                    id="mp__bank-name"
                    type="text"
                    name="name"
                    className="w-[19.625rem] max-[900px]:w-full"
                    maxLength={12}
                    onChange={handleChangeBankSuggestion}
                    value={suggestionBank.name}
                    rules={[(val: HtmlInput) => validations.required(val, 'Nome do banco')]}
                  />
                </div>
              </div>
              <div className="flex justify-between">
                <Button
                  type="button"
                  text="Sugerir banco"
                  variant="primary"
                  classNames="w-full"
                  id="mp__bank-data-next_button"
                  onClick={registerBankSuggestion}
                  disabled={disableButton}
                />
              </div>
            </>
          )}
          {selected.label && selected.label !== 'Não encontrei meu banco' && (
            <>
              <div className="flex flex-col gap-4">
                <div className="relative flex justify-between mb-6">
                  <div className="w-[347px] mr-4 max-[500px]:w-full max-[500px]:mr-4">
                    <Input
                      label="Agência"
                      id="mp__bank-data-agency_input"
                      type="text"
                      name="agency"
                      className=""
                      rules={[
                        (val: HtmlInput) => validations.required(val, 'Agência'),
                        (val: HtmlInput) => validations.isNotZero(val, 'Agência'),
                      ]}
                      onChange={handleChange}
                      value={(data && data.agency) || ''}
                      maxLength={4}
                    />
                  </div>
                  <Input
                    label="Dígito"
                    id="mp__bank-data-agency-digit_input"
                    type="text"
                    name="agency_digit"
                    children={
                      <Tooltip
                        text={
                          <>
                            Se o dígito for "X", <strong>substitua por 0</strong>
                          </>
                        }
                        className="absolute top-[43px] right-[7px]"
                        onClick={() => gtmEventsTracker(gtmAttributesTooltipAgency)}
                      />
                    }
                    className="w-[106px]"
                    maxLength={1}
                    onChange={handleChange}
                    value={(data && data.agency_digit) || ''}
                  />
                </div>
                <div className="relative flex justify-between mb-12">
                  <div className="w-[347px] max-[500px]:w-full">
                    <Input
                      label="Conta corrente"
                      id="mp__bank-data-account_input"
                      type="text"
                      name="account"
                      className=""
                      maxLength={12}
                      rules={[
                        (val: HtmlInput) => validations.required(val, 'Conta corrente'),
                        (val: HtmlInput) => validations.isNotZero(val, 'Conta corrente'),
                      ]}
                      onChange={handleChange}
                      value={(data.account && data.account) || ''}
                    />
                  </div>
                  <div className="ml-5 max-[500px]:ml-0 w-[36%]">
                    <Input
                      label="Dígito"
                      id="mp__bank-data-account-digit_input"
                      type="text"
                      name="account_digit"
                      children={
                        <Tooltip
                          text={
                            <>
                              Se o dígito for "X", <strong>substitua por 0</strong>
                            </>
                          }
                          className="absolute top-[43px] right-[7px]"
                          onClick={() => gtmEventsTracker(gtmAttributesTooltipAccount)}
                        />
                      }
                      className="w-[106px]"
                      maxLength={1}
                      rules={[(val: HtmlInput) => validations.required(val, 'Digito da conta')]}
                      onChange={handleChange}
                      value={(data.account_digit && data.account_digit) || ''}
                    />
                  </div>
                </div>
              </div>
              <div className="flex gap-4 max-[500px]:flex-col-reverse">
                <div className="w-[227px] max-[500px]:w-full">
                  <Button
                    type="submit"
                    text="Continuar"
                    variant="primary"
                    id="mp__bank-data-next_button"
                    disabled={disableButton}
                  />
                </div>
              </div>
            </>
          )}
        </form>
      </div>
      <div className="absolute top-[3.688rem] right-[5.5rem] w-[23.625rem] max-[913px]:top-[4.688rem] max-[913px]:w-full max-[913px]:right-0 max-[913px]:px-5 max-[913px]:py-6">
        <Alert
          show={showMessage}
          variant={variant}
          onClose={() => {
            setShowMessage(false);
            setMessage('Carregando...');
          }}
          dismissible
        >
          <p>{message}</p>
        </Alert>
      </div>
    </>
  );
};

export default BankData;
